import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import {
  CheckCircle2,
  Users,
  Layers,
  AlertCircle,
  BarChart3,
  MessageSquare,
  Search,
  ClipboardList,
  Settings2,
  ShieldCheck,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import apiService from '@/services/api';

const HowItWorksPage = () => {
  const [, setSections] = useState<any>({});

  useEffect(() => {
    const fetchPageSections = async () => {
      try {
        const response = await apiService.get('/public/page-sections/how-it-works');
        if (response.success && Array.isArray(response.data)) {
          const sectionMap: Record<string, any> = {};
          response.data.forEach((section: any) => {
            try {
              sectionMap[section.section_key] = {
                ...section,
                body: section.body ? JSON.parse(section.body) : section.body,
              };
            } catch (e) {
              sectionMap[section.section_key] = section;
            }
          });
          setSections(sectionMap);
        }
      } catch (error) {
        console.error('Error fetching page sections:', error);
      }
    };
    fetchPageSections();
  }, []);

  const pillars = [
    {
      icon: Search,
      title: 'Discovery & Curation',
      desc: 'We begin by deeply understanding your requirements. From property specifics to timeline preferences, we gather the context needed to match you with the perfect provider.',
      bullets: [
        'Initial consultation and requirements gathering',
        'Provider selection based on expertise and availability',
      ],
    },
    {
      icon: ClipboardList,
      title: 'Strategic Planning',
      desc: 'Once matched, we develop a detailed service plan. This includes scope definition, timeline, resource allocation, and contingency planning.',
      bullets: [
        'Detailed scope and proposal development',
        'Budget and timeline agreement with all stakeholders',
      ],
    },
    {
      icon: Settings2,
      title: 'Active Management',
      desc: "We don't just connect parties—we actively manage the engagement. Real-time communication, quality checks, and proactive problem-solving ensure smooth execution.",
      bullets: [
        'On-site coordination and real-time status updates',
        'Quality assurance and issue resolution',
      ],
    },
    {
      icon: ShieldCheck,
      title: 'Post-Service Audit',
      desc: 'After completion, we conduct thorough quality verification. Documentation, photo evidence, and stakeholder feedback are captured for records and continuous improvement.',
      bullets: [
        'Completion verification and documentation',
        'Performance review and feedback collection',
      ],
    },
  ];

  const faqs = [
    {
      q: 'Why is the managed model better?',
      a: 'Traditional hiring puts all responsibility on you—vetting, coordination, and problem-solving. The managed model transfers this complexity to us, giving you peace of mind and better outcomes.',
    },
    {
      q: 'How do you ensure quality?',
      a: 'Every provider is vetted through background checks, reference verification, and skill assessment. We maintain continuous oversight through quality checks and stakeholder feedback.',
    },
    {
      q: "What's included in management?",
      a: 'Coordination, communication with all parties, real-time status updates, problem-solving, quality assurance, and documentation. We handle everything between booking and completion.',
    },
    {
      q: 'What if something goes wrong?',
      a: "We immediately investigate and take corrective action—whether that's rework, replacement, or compensation. You're protected by our guarantee.",
    },
  ];

  const stakeholders = [
    {
      title: 'Homeowners',
      desc: 'Delegate property management responsibilities with confidence. Get reliable service without the hiring hassle.',
      gradient: 'from-teal-500 to-emerald-600',
      icon: '🏠',
    },
    {
      title: 'Real Estate Agents',
      desc: 'Enhance tenant experience and property management. Coordinate services seamlessly across multiple properties.',
      gradient: 'from-teal-600 to-cyan-600',
      icon: '🏢',
    },
    {
      title: 'Property Managers',
      desc: 'Streamline operations and reduce staffing burden. Access vetted service providers on-demand through one platform.',
      gradient: 'from-emerald-600 to-teal-700',
      icon: '👥',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-white overflow-hidden">
        <Helmet>
          <title>How It Works | Virewire Managed Services</title>
          <meta
            name="description"
            content="Discover how Virewire's platform-managed services work. From discovery to completion, we handle every detail so you don't have to."
          />
          <link rel="canonical" href="https://virewire.co.uk/how-it-works" />
        </Helmet>

        {/* ===== HERO SECTION ===== */}
        <section className="relative isolate overflow-hidden bg-gradient-to-br from-slate-950 via-teal-950 to-teal-900 text-white">
          <div className="absolute inset-0 -z-10">
            <div className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-teal-500/20 blur-3xl" />
            <div className="absolute -bottom-40 -right-20 h-[36rem] w-[36rem] rounded-full bg-emerald-400/15 blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.07]"
            style={{
              backgroundImage:
                'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
              backgroundSize: '56px 56px',
            }}
          />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wide text-teal-100 backdrop-blur-md mb-6">
                  <Sparkles className="h-3.5 w-3.5 text-emerald-300" />
                  How Virewire Works
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05] tracking-tight">
                  You're not hiring contractors —
                  <span className="block bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-200 bg-clip-text text-transparent">
                    you're buying outcomes.
                  </span>
                </h1>
                <p className="text-lg text-teal-100/90 mb-8 leading-relaxed max-w-xl">
                  Virewire streamlines the full lifecycle of management services. We shoulder the burden of coordination, vetting, and problem-solving on behalf of multiple stakeholders.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button className="group bg-white text-teal-800 hover:bg-teal-50 font-semibold px-8 h-12 text-base shadow-xl shadow-teal-950/30" asChild>
                    <Link to="/request-service">
                      Request a Service
                      <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white/30 bg-white/5 text-white hover:bg-white/15 hover:border-white/50 font-semibold px-8 h-12 text-base backdrop-blur-md"
                    asChild
                  >
                    <Link to="/contact">Talk to Us</Link>
                  </Button>
                </div>
              </div>

              {/* Visual composition */}
              <div className="relative h-[26rem] lg:h-[28rem]">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-teal-700/60 via-teal-800/60 to-emerald-900/60 backdrop-blur-md border border-white/10" />
                <div className="absolute -top-10 -right-10 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />
                <div className="absolute -bottom-10 -left-10 h-72 w-72 rounded-full bg-cyan-400/15 blur-3xl" />

                {/* Floating step cards */}
                {[
                  { num: '01', label: 'Discovery', icon: Search, top: 'top-8', side: 'left-6' },
                  { num: '02', label: 'Planning', icon: ClipboardList, top: 'top-32', side: 'right-6' },
                  { num: '03', label: 'Execution', icon: Settings2, top: 'top-56', side: 'left-12' },
                  { num: '04', label: 'Audit', icon: ShieldCheck, top: 'bottom-8', side: 'right-12' },
                ].map(({ num, label, icon: Icon, top, side }) => (
                  <div
                    key={num}
                    className={`absolute ${top} ${side} rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-4 py-3 shadow-xl`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-400/30 to-teal-500/30 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-emerald-200 text-[10px] font-bold tracking-widest">STEP {num}</p>
                        <p className="text-white text-sm font-semibold">{label}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Center pulse */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 -m-8 rounded-full bg-white/5 animate-pulse" />
                    <div className="relative h-20 w-20 rounded-full bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border border-white/30 flex items-center justify-center">
                      <BarChart3 className="w-10 h-10 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <svg
            className="absolute bottom-0 left-0 right-0 h-16 w-full text-white"
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0,32 C240,80 480,80 720,48 C960,16 1200,16 1440,40 L1440,80 L0,80 Z" />
          </svg>
        </section>

        {/* ===== THE MANAGED MARKETPLACE MODEL ===== */}
        <section className="py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">Our Approach</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                The Managed Marketplace Model
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Traditional marketplaces connect buyers and sellers. We go further by managing the entire engagement.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Users,
                  title: 'Buyer Confidence',
                  desc: 'Vetted providers, transparent pricing, guaranteed outcomes, and dedicated support throughout the engagement.',
                },
                {
                  icon: Layers,
                  title: 'Provider Enablement',
                  desc: 'Pre-qualified leads, simplified operations, transparent processes, and fair compensation for quality work.',
                },
                {
                  icon: BarChart3,
                  title: 'Platform Economics',
                  desc: 'Digital infrastructure, rating systems, dispute resolution, and data-driven insights that benefit all parties.',
                },
              ].map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200 p-8 transition-all hover:-translate-y-1 hover:border-teal-200 hover:shadow-xl hover:shadow-teal-900/5"
                >
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-teal-50 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-50 to-emerald-50 ring-1 ring-teal-100">
                      <Icon className="text-teal-700" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
                    <p className="text-gray-600 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== THE FOUR PILLARS ===== */}
        <section className="relative bg-gradient-to-b from-gray-50 to-white py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">The Methodology</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                The Four Pillars of Execution
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Every engagement follows our proven methodology
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {pillars.map(({ icon: Icon, title, desc, bullets }, idx) => (
                <div
                  key={title}
                  className="relative overflow-hidden rounded-2xl bg-white border border-gray-200 p-8 hover:shadow-xl hover:shadow-teal-900/5 transition-shadow"
                >
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0">
                      <div className="relative h-14 w-14">
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 shadow-lg shadow-teal-500/30" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Icon className="h-7 w-7 text-white" />
                        </div>
                      </div>
                      <div className="mt-3 text-center">
                        <span className="text-xs font-bold tracking-widest text-teal-600">
                          0{idx + 1}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{desc}</p>
                      <ul className="space-y-2">
                        {bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle2 size={18} className="text-teal-600 flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== KEY FEATURES ===== */}
        <section className="py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-50 via-white to-emerald-50/50 border border-teal-100 p-8 lg:p-10 transition-shadow hover:shadow-xl">
                <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-teal-100/60 blur-2xl" />
                <div className="relative">
                  <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 shadow-lg shadow-teal-500/30">
                    <BarChart3 className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Controlled Dashboard</h3>
                  <p className="text-gray-600 mb-5 leading-relaxed">
                    All stakeholders access a single source of truth. Track engagement status, manage communications, and access documentation—all in one intuitive interface.
                  </p>
                  <ul className="space-y-2">
                    {[
                      'Real-time status tracking and updates',
                      'Document management and archival',
                      'Communication hub with all parties',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-gray-700">
                        <CheckCircle2 size={18} className="text-teal-600 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-teal-50/50 border border-emerald-100 p-8 lg:p-10 transition-shadow hover:shadow-xl">
                <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emerald-100/60 blur-2xl" />
                <div className="relative">
                  <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/30">
                    <MessageSquare className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
                  <p className="text-gray-600 mb-5 leading-relaxed">
                    When questions arise, changes occur, or issues need resolution—we're here. Our operations team provides dedicated support throughout every engagement.
                  </p>
                  <ul className="space-y-2">
                    {[
                      'Dedicated support team for your needs',
                      'Change management and rescheduling',
                      'Issue escalation and resolution',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-gray-700">
                        <CheckCircle2 size={18} className="text-teal-600 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== CLARIFYING THE DIFFERENCE ===== */}
        <section className="bg-gradient-to-b from-gray-50 to-white py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">Common Questions</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                Clarifying the Difference
              </h2>
              <p className="text-lg text-gray-600">
                How platform-managed services compare to traditional approaches
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <div
                  key={q}
                  className="group rounded-2xl border border-gray-200 bg-white p-6 transition-all hover:border-teal-200 hover:shadow-md"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-teal-50 to-emerald-50 ring-1 ring-teal-100 flex items-center justify-center group-hover:from-teal-100 group-hover:to-emerald-100 transition-colors">
                        <AlertCircle className="text-teal-700" size={18} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">{q}</h4>
                      <p className="text-gray-600 leading-relaxed">{a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== TAILORED FOR EVERY STAKEHOLDER ===== */}
        <section className="py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">For Everyone</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                Tailored for Every Stakeholder
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Different users, different needs. One platform handles them all.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {stakeholders.map(({ title, desc, gradient, icon }) => (
                <div
                  key={title}
                  className="group rounded-2xl bg-white border border-gray-200 overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-teal-900/10"
                >
                  <div className={`relative h-48 bg-gradient-to-br ${gradient} overflow-hidden`}>
                    <div
                      className="absolute inset-0 opacity-[0.12]"
                      style={{
                        backgroundImage:
                          'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
                        backgroundSize: '24px 24px',
                      }}
                    />
                    <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-white/15 blur-2xl" />
                    <div className="relative h-full flex items-center justify-center text-7xl">{icon}</div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                    <p className="text-gray-600 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="bg-gradient-to-b from-gray-50 to-white py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">Help Center</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-3">
              {[
                {
                  q: 'How do I request a service?',
                  a: 'Simply fill out our request form with details about your needs. Our team reviews it and matches you with the best provider. You will receive a proposal within 24-48 hours.',
                },
                {
                  q: 'Can I change or cancel my booking?',
                  a: 'Yes. Just notify us as soon as possible. Changes made with 48+ hours notice are accommodated at no penalty. Our support team will help reschedule or modify your service.',
                },
                {
                  q: 'How are prices determined?',
                  a: 'Pricing is based on service type, scope, location, and provider expertise. You will receive a fixed, transparent quote upfront with no hidden fees or surprises.',
                },
                {
                  q: 'What if I am not satisfied with the work?',
                  a: 'We guarantee your satisfaction. If issues arise, contact us immediately. We will rework it or arrange a replacement at no additional cost. Your satisfaction is our priority.',
                },
              ].map(({ q, a }) => (
                <div
                  key={q}
                  className="rounded-xl border border-gray-200 bg-white p-6 transition-colors hover:border-teal-200"
                >
                  <h4 className="font-bold text-gray-900 mb-2">{q}</h4>
                  <p className="text-gray-600 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="px-4 sm:px-6 lg:px-8 pb-24">
          <div className="relative max-w-5xl mx-auto rounded-[2rem] overflow-hidden shadow-2xl shadow-teal-900/20">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-700" />
            <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl" />
            <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" preserveAspectRatio="none">
              <defs>
                <pattern id="diagonal-stripe-hiw" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                  <line x1="0" y1="0" x2="0" y2="20" stroke="white" strokeWidth="10" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#diagonal-stripe-hiw)" />
            </svg>

            <div className="relative z-10 max-w-4xl mx-auto text-center px-8 py-20 lg:py-24 text-white">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-wide backdrop-blur-md mb-6">
                <Sparkles className="h-3.5 w-3.5" />
                Ready when you are
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 tracking-tight">
                Ready to Unify Your Management?
              </h2>
              <p className="text-lg text-teal-50 mb-10 max-w-2xl mx-auto leading-relaxed">
                Experience the difference of platform-managed services. Get started today.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button className="group bg-white text-teal-800 hover:bg-teal-50 font-semibold px-8 h-12 text-base shadow-xl" asChild>
                  <Link to="/request-service">
                    Request a Service
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  className="border-white/40 bg-white/5 text-white hover:bg-white/15 hover:border-white/60 font-semibold px-8 h-12 text-base backdrop-blur-md"
                  asChild
                >
                  <Link to="/contact">Contact Our Team</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HowItWorksPage;
